import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { playNewOrderSound } from '@/utils/soundUtils';
import type { Order } from '@/types/index';

interface UseOrderNotificationsProps {
    orders: Order[];
    soundEnabled: boolean;
    activeTab: string;
    loading: boolean;
}

export const useOrderNotifications = ({
    orders,
    soundEnabled,
    activeTab,
    loading
}: UseOrderNotificationsProps) => {
    const { toast } = useToast();

    const [deliveryNotificationEnabled, setDeliveryNotificationEnabled] = useState(true);
    const [newDeliveryOrderCount, setNewDeliveryOrderCount] = useState(0);
    const [activeOrdersNotificationEnabled, setActiveOrdersNotificationEnabled] = useState(true);
    const [newActiveOrderCount, setNewActiveOrderCount] = useState(0);

    const pendingDeliveryOrderCount = useMemo(() => {
        return orders.filter(order =>
            order.order_type === 'delivery' &&
            order.status === 'pending'
        ).length;
    }, [orders]);

    const pendingActiveOrderCount = useMemo(() => {
        return orders.filter(order =>
            order.order_type !== 'delivery' &&
            order.status === 'pending'
        ).length;
    }, [orders]);

    // Track total unacknowledged items across all orders for notifications
    const totalUnacknowledgedItems = useMemo(() => {
        return orders.reduce((total, order) => {
            const unacknowledgedCount = order.order_items?.filter(
                item => !item.is_acknowledged && !item.is_removed
            ).length || 0;
            return total + unacknowledgedCount;
        }, 0);
    }, [orders]);

    // Track new delivery orders
    const [previousDeliveryOrderCount, setPreviousDeliveryOrderCount] = useState(0);
    useEffect(() => {
        if (loading) return;
        const currentDeliveryCount = pendingDeliveryOrderCount;

        if (previousDeliveryOrderCount === 0) {
            setPreviousDeliveryOrderCount(currentDeliveryCount);
            return;
        }

        if (currentDeliveryCount > previousDeliveryOrderCount && soundEnabled && deliveryNotificationEnabled) {
            const newCount = currentDeliveryCount - previousDeliveryOrderCount;
            playNewOrderSound();
            toast({
                title: "📦 New Delivery Orders!",
                description: `${newCount} new delivery order(s) received!`,
                duration: 5000
            });

            if (activeTab !== 'delivery') {
                setNewDeliveryOrderCount(newCount);
            }
        }
        setPreviousDeliveryOrderCount(currentDeliveryCount);
    }, [pendingDeliveryOrderCount, previousDeliveryOrderCount, soundEnabled, deliveryNotificationEnabled, loading, activeTab, toast]);

    // Reset delivery notification count
    useEffect(() => {
        if (activeTab === 'delivery') {
            setNewDeliveryOrderCount(0);
        }
    }, [activeTab]);

    // Track new active orders AND order updates (new items)
    const [previousActiveOrderCount, setPreviousActiveOrderCount] = useState(0);
    const [previousUnacknowledgedItems, setPreviousUnacknowledgedItems] = useState(0);
    
    useEffect(() => {
        if (loading) return;
        const currentActiveCount = pendingActiveOrderCount;
        const currentUnacknowledgedItems = totalUnacknowledgedItems;

        // Initialize on first load
        if (previousActiveOrderCount === 0 && previousUnacknowledgedItems === 0) {
            setPreviousActiveOrderCount(currentActiveCount);
            setPreviousUnacknowledgedItems(currentUnacknowledgedItems);
            return;
        }

        // Check for new orders OR new items added to existing orders
        const hasNewOrders = currentActiveCount > previousActiveOrderCount;
        const hasNewItems = currentUnacknowledgedItems > previousUnacknowledgedItems && !hasNewOrders;

        if ((hasNewOrders || hasNewItems) && soundEnabled && activeOrdersNotificationEnabled) {
            playNewOrderSound();
            
            if (hasNewOrders) {
                const newCount = currentActiveCount - previousActiveOrderCount;
                toast({
                    title: "🛎️ New Orders!",
                    description: `${newCount} new order(s) received!`,
                    duration: 5000
                });
            } else if (hasNewItems) {
                const newItemsCount = currentUnacknowledgedItems - previousUnacknowledgedItems;
                toast({
                    title: "📝 Order Updated!",
                    description: `${newItemsCount} new item(s) added to existing order!`,
                    duration: 5000
                });
            }

            if (activeTab !== 'active') {
                setNewActiveOrderCount(prev => prev + 1);
            }
        }
        
        setPreviousActiveOrderCount(currentActiveCount);
        setPreviousUnacknowledgedItems(currentUnacknowledgedItems);
    }, [pendingActiveOrderCount, totalUnacknowledgedItems, previousActiveOrderCount, previousUnacknowledgedItems, soundEnabled, activeOrdersNotificationEnabled, loading, activeTab, toast]);

    // Reset active order notification count
    useEffect(() => {
        if (activeTab === 'active') {
            setNewActiveOrderCount(0);
        }
    }, [activeTab]);

    return {
        deliveryNotificationEnabled,
        setDeliveryNotificationEnabled,
        newDeliveryOrderCount,
        activeOrdersNotificationEnabled,
        setActiveOrdersNotificationEnabled,
        newActiveOrderCount,
        pendingDeliveryOrderCount
    };
};
